"use client";

import { Button, Drawer, IconButton, Link } from "@repo/components";
import { Menu as MenuIcon, X as CloseIcon } from "lucide-react";
import type { ReactNode } from "react";
import { css } from "@repo/styled-system/css";
import { HStack, Stack, styled } from "@repo/styled-system/jsx";

export interface NavbarLink {
  /** Visible label for the navigation item. */
  label: string;
  /** Destination URL. */
  href: string;
  /** Marks the link as the active/current page. */
  active?: boolean;
}

export interface NavbarProps {
  /** Brand mark rendered on the left. Pass a logo node or text. */
  logo?: ReactNode;
  /** Href the brand mark links to. @default "/" */
  logoHref?: string;
  /** Primary navigation links. */
  links: NavbarLink[];
  /** Label for the primary call-to-action button. */
  ctaLabel: string;
  /** Invoked when the primary CTA is pressed. */
  onCta: () => void;
  /** Invoked when the sign-in button is pressed. */
  onSignIn: () => void;
  /** Label for the sign-in button. @default "Sign in" */
  signInLabel?: string;
}

const Nav = styled("nav");

/**
 * Navbar — presentational top marketing navigation.
 *
 * Renders a logo, horizontal nav links, sign-in + primary CTA buttons, and a
 * mobile drawer. Purely presentational: all data and callbacks arrive via props.
 */
export function Navbar(props: NavbarProps) {
  const {
    logo = "Acme",
    logoHref = "/",
    links,
    ctaLabel,
    onCta,
    onSignIn,
    signInLabel = "Sign in",
  } = props;

  return (
    <Nav
      className={css({
        position: "sticky",
        top: "0",
        zIndex: "sticky",
        bg: "bg.canvas",
        borderBottomWidth: "1px",
        borderColor: "border.default",
        color: "fg.default",
      })}
    >
      <HStack
        justify="space-between"
        gap="6"
        px={{ base: "4", md: "8" }}
        py="3"
        maxW="7xl"
        mx="auto"
        h="16"
      >
        <Link
          href={logoHref}
          fontWeight="bold"
          fontSize="lg"
          color="fg.default"
          textDecoration="none"
          _hover={{ color: "colorPalette.text" }}
        >
          {logo}
        </Link>

        {/* Desktop links */}
        <HStack as="ul" gap="1" display={{ base: "none", md: "flex" }} listStyleType="none">
          {links.map((link) => (
            <li key={link.href}>
              <Link
                href={link.href}
                px="3"
                py="2"
                fontSize="sm"
                fontWeight="medium"
                textDecoration="none"
                color={link.active ? "fg.default" : "fg.muted"}
                aria-current={link.active ? "page" : undefined}
                _hover={{ color: "fg.default" }}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </HStack>

        {/* Desktop actions */}
        <HStack gap="3" display={{ base: "none", md: "flex" }}>
          <Button variant="ghost" size="sm" onClick={onSignIn}>
            {signInLabel}
          </Button>
          <Button variant="solid" size="sm" onClick={onCta}>
            {ctaLabel}
          </Button>
        </HStack>

        {/* Mobile drawer */}
        <Drawer.Root>
          <Drawer.Trigger asChild>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="Open navigation menu"
              display={{ base: "inline-flex", md: "none" }}
            >
              <MenuIcon />
            </IconButton>
          </Drawer.Trigger>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.Header>
                <Drawer.Title>{logo}</Drawer.Title>
                <Drawer.CloseTrigger asChild position="absolute" top="3" right="4">
                  <IconButton variant="ghost" size="sm" aria-label="Close navigation menu">
                    <CloseIcon />
                  </IconButton>
                </Drawer.CloseTrigger>
              </Drawer.Header>
              <Drawer.Body>
                <Stack as="ul" gap="1" listStyleType="none">
                  {links.map((link) => (
                    <li key={link.href}>
                      <Drawer.CloseTrigger asChild>
                        <Link
                          href={link.href}
                          display="block"
                          px="3"
                          py="2"
                          rounded="md"
                          fontSize="md"
                          fontWeight="medium"
                          textDecoration="none"
                          color={link.active ? "fg.default" : "fg.muted"}
                          aria-current={link.active ? "page" : undefined}
                          _hover={{ bg: "bg.muted", color: "fg.default" }}
                        >
                          {link.label}
                        </Link>
                      </Drawer.CloseTrigger>
                    </li>
                  ))}
                </Stack>
              </Drawer.Body>
              <Drawer.Footer>
                <Stack gap="3" width="full">
                  <Drawer.CloseTrigger asChild>
                    <Button variant="outline" width="full" onClick={onSignIn}>
                      {signInLabel}
                    </Button>
                  </Drawer.CloseTrigger>
                  <Drawer.CloseTrigger asChild>
                    <Button variant="solid" width="full" onClick={onCta}>
                      {ctaLabel}
                    </Button>
                  </Drawer.CloseTrigger>
                </Stack>
              </Drawer.Footer>
            </Drawer.Content>
          </Drawer.Positioner>
        </Drawer.Root>
      </HStack>
    </Nav>
  );
}
